/**
 * Lightweight browser notification helper.
 *
 * Uses the standard Web Notifications API — no service worker or VAPID needed.
 * Works reliably when the tab is open in the background (99% of "did the user
 * step away?" cases). If the tab is fully closed we cannot push without
 * additional Service Worker + Push Subscription plumbing (future work).
 */

const STORAGE_KEY = "kaan_notification_permission";

export const notificationsSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

export const getPermission = () => {
  if (!notificationsSupported()) return "unsupported";
  return Notification.permission;
};

/** Ask permission once; remember so we don't keep prompting. */
export const requestPermissionIfNeeded = async () => {
  if (!notificationsSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  const already = localStorage.getItem(STORAGE_KEY);
  if (already === "asked") return Notification.permission;
  try {
    const perm = await Notification.requestPermission();
    localStorage.setItem(STORAGE_KEY, "asked");
    return perm;
  } catch {
    return "default";
  }
};

/**
 * Show a notification (best-effort). Only fires if:
 *  - permission is granted, AND
 *  - the current tab is not visible (user is elsewhere)
 * If the tab IS visible we let the in-app toast handle it.
 */
export const notifyIfHidden = ({ title, body, tag, onClick }) => {
  if (!notificationsSupported()) return false;
  if (Notification.permission !== "granted") return false;
  if (document.visibilityState === "visible") return false;
  try {
    const n = new Notification(title, {
      body,
      icon: "/logo192.png",
      badge: "/logo192.png",
      tag: tag || "kaangpt",
      requireInteraction: true,
    });
    n.onclick = () => {
      window.focus();
      n.close();
      onClick?.();
    };
    return true;
  } catch {
    return false;
  }
};
